import { api } from 'redux-store/api';
import queryString from 'query-string';
import {
  ICreateProfileBody,
  IGetProfileByUserIdArgs,
  IGetProfilesResponse,
  IMutateProfileResponse,
  IProfile,
  IUpdateProfileBody,
  IUpdateProfilesProjectsArgs,
  IUpdateSoftDeletedFromCompanyArgs,
} from './';

enum Endpoints {
  PROFILES = 'profiles/',
}

export const profilesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProfileByUserId: build.query<IProfile, IGetProfileByUserIdArgs>({
      query: (queryArgs) => ({
        url: `${Endpoints.PROFILES}${queryArgs.userId}`,
      }),
      providesTags: (res, err) => (res && !err ? [{ type: 'Profiles', id: res.id }] : []),
    }),
    getProfiles: build.query<IGetProfilesResponse, void>({
      query: () => ({
        url: Endpoints.PROFILES,
      }),
      providesTags: (res) => (res ? [{ type: 'Profiles', id: 'LIST' }] : []),
    }),
    createProfile: build.mutation<IMutateProfileResponse, ICreateProfileBody>({
      query: ({ data }) => ({
        url: Endpoints.PROFILES,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),

    updateMyProfile: build.mutation<IProfile, IUpdateProfileBody>({
      query: ({ id, data }) => ({
        url: `${Endpoints.PROFILES}${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['User', 'Profiles'],
    }),

    updateProfile: build.mutation<IProfile, IUpdateProfileBody>({
      query: ({ id, data }) => ({
        url: `${Endpoints.PROFILES}profiles/${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['User', 'Profiles'],
    }),

    updateProfileMe: build.mutation<IMutateProfileResponse, IUpdateProfileBody>({
      query: ({ id, data }) => ({
        url: `${Endpoints.PROFILES}me/${id}`,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: ['User'],
    }),
    updateProfilesProjects: build.mutation<undefined, IUpdateProfilesProjectsArgs>({
      query: ({ id, ...data }) => ({
        url: `${Endpoints.PROFILES}${id}/projects`,
        body: data,
        method: 'PATCH',
      }),
    }),
    updateProfilesSoftDeletedFromCompany: build.mutation<
      undefined,
      IUpdateSoftDeletedFromCompanyArgs
    >({
      query: ({ action, ids }) => ({
        url: `${Endpoints.PROFILES}soft-deleted-from-company?${queryString.stringify(
          { ids },
          { arrayFormat: 'bracket' }
        )}&action=${action}`,
        method: 'PATCH',
      }),
      onQueryStarted: async ({ ids, action }, { dispatch, queryFulfilled }) => {
        const patchProfiles = dispatch(
          profilesApi.util.updateQueryData('getProfiles', undefined, (draft) => {
            if (draft?.items) {
              const itemIndex = draft.items.findIndex(({ id }) => id === ids[0]);
              if (itemIndex >= 0) {
                const roleIndex = draft.items[itemIndex].roles.findIndex(({ companyId }) => {
                  return localStorage.getItem('company-id') === `${companyId}`;
                });

                if (roleIndex >= 0) {
                  draft.items[itemIndex].roles[roleIndex].deletedAt =
                    action === 'DELETE' ? new Date().toISOString() : null;
                }
              }
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchProfiles?.undo();
        }
      },
      invalidatesTags: (_, err, args) => {
        if (err) {
          return [];
        }

        const tags: Array<'Profiles' | 'Current Plan' | { type: 'Profiles'; id: number | 'LIST' }> =
          ['Current Plan'];

        if (args?.invalidate === 'none') {
          return tags;
        }

        if (!args?.invalidate || args.invalidate === 'all') {
          return ['Profiles', ...tags];
        }

        if (args.invalidate === 'id') {
          return [{ type: 'Profiles', id: args.ids[0] }, ...tags];
        }

        if (args.invalidate === 'list') {
          return [{ type: 'Profiles', id: 'LIST' }, ...tags];
        }

        return tags;
      },
    }),
  }),
});

export const {
  useGetProfileByUserIdQuery,
  useLazyGetProfileByUserIdQuery,
  useGetProfilesQuery,
  useLazyGetProfilesQuery,
  useCreateProfileMutation,
  useUpdateProfileMeMutation,
  useUpdateProfileMutation,
  useUpdateMyProfileMutation,
  useUpdateProfilesProjectsMutation,
  useUpdateProfilesSoftDeletedFromCompanyMutation,
} = profilesApi;
